// The command bar: what is typed into it, its history and height, and the
// command search above it (docs/claude/tools.md). Split out of main.ts
// (docs/claude/uiModules.md).
import {
  type CommandDef,
  COMMANDS,
  type CommandSearchEntry,
  looksLikeEntry,
  resolveCommand,
  resolveOption,
  searchCommands,
} from '../commands';
import {
  invalidate,
  state,
} from './core';
import { tr } from '../i18n';
import {
  $,
  applyToolOption,
  repeatLastTool,
  runCommandButton,
  toolOptions,
} from '../main';
import {
  commitDistEntry,
  drawDraft,
  pendingRectCorner,
} from './distanceEntry';
import { setChannelNumber } from './lightTools';
import { setSculptNumber } from './sculptBenchUi';

// ---- Command bar (AutoCAD-style command line) ----
// Its prompt IS #status-tool (index.html), so every tool hint and flash lands in
// the bar with no per-tool code. Here: its history, running a typed command
// (commands.ts — a command clicks its toolbar button), and repeating the last tool.
const CMD_HISTORY_MAX = 200;
const cmdHistory: string[] = [];
/** Append a line to the command history. `dedupe` drops a repeat of the last
 *  line — for messages that may be flashed on every mousemove. The whole history
 *  is listed; the bar's height decides how much shows (scroll for the rest). */
export function cmdLog(line: string, dedupe = false): void {
  if (!line || (dedupe && cmdHistory[cmdHistory.length - 1] === line)) return;
  cmdHistory.push(line);
  const el = document.getElementById('cmd-history');
  if (!el) { if (cmdHistory.length > CMD_HISTORY_MAX) cmdHistory.shift(); return; }
  const atEnd = el.scrollTop + el.clientHeight >= el.scrollHeight - 2;
  const d = document.createElement('div');
  d.textContent = line;
  el.appendChild(d);
  if (cmdHistory.length > CMD_HISTORY_MAX) { cmdHistory.shift(); el.firstElementChild?.remove(); }
  if (atEnd) el.scrollTop = el.scrollHeight; // a user reading back is left where they are
}

// The bar's height is a whole number of history lines (`--cmdbar-h` on #app, a
// grid row): even, so the drawing keeps its pixel parity. 0 lines = collapsed to
// the prompt alone. Dragged at its top edge, toggled by double-click or ▾ / ▴.
const CMD_LINE_PX = 16;
const CMD_BASE_PX = 28;           // the prompt line + borders: the bar with no history
const CMD_LINES_DEFAULT = 3;
const CMD_LINES_KEY = 'stenal.cmdbarLines';
const CMD_DRAWING_MIN_PX = 160;   // never squeeze the drawing below this
let cmdLines = CMD_LINES_DEFAULT;
let cmdLinesOpen = CMD_LINES_DEFAULT; // what a double-click / ▴ brings back
/** Show `n` lines of history (clamped so the drawing keeps some room). */
function setCmdbarLines(n: number, save = true): void {
  const drawingH = document.getElementById('drawing')?.getBoundingClientRect().height ?? 0;
  const max = drawingH > 0 ? cmdLines + Math.floor((drawingH - CMD_DRAWING_MIN_PX) / CMD_LINE_PX) : 40;
  cmdLines = Math.max(0, Math.min(Math.round(n), Math.max(cmdLines, max)));
  if (cmdLines > 0) cmdLinesOpen = cmdLines;
  $('app').style.setProperty('--cmdbar-h', `${CMD_BASE_PX + CMD_LINE_PX * cmdLines}px`);
  $('cmdbar').classList.toggle('collapsed', cmdLines === 0);
  const toggle = $('cmd-history-toggle');
  toggle.textContent = cmdLines === 0 ? '▴' : '▾';
  toggle.title = tr(cmdLines === 0 ? 'Show command history' : 'Hide command history');
  const hist = $('cmd-history');
  hist.scrollTop = hist.scrollHeight;
  if (save) try { localStorage.setItem(CMD_LINES_KEY, String(cmdLines)); } catch { /* storage blocked */ }
}
function toggleCmdHistory(): void { setCmdbarLines(cmdLines === 0 ? cmdLinesOpen : 0); }
function installCmdbarResize(): void {
  let saved = NaN;
  try { saved = Number(localStorage.getItem(CMD_LINES_KEY) ?? NaN); } catch { /* storage blocked */ }
  if (Number.isFinite(saved) && saved > 0) cmdLinesOpen = saved;
  setCmdbarLines(Number.isFinite(saved) ? saved : CMD_LINES_DEFAULT, false);
  const handle = $('cmd-resize');
  handle.addEventListener('pointerdown', (e) => {
    if (e.button !== 0) return;
    e.preventDefault();
    const y0 = e.clientY, lines0 = cmdLines;
    handle.classList.add('dragging');
    try { handle.setPointerCapture(e.pointerId); } catch { /* capture unavailable — the listeners still track it */ }
    const move = (ev: PointerEvent): void => setCmdbarLines(lines0 + (y0 - ev.clientY) / CMD_LINE_PX, false);
    const up = (ev: PointerEvent): void => {
      try { handle.releasePointerCapture(ev.pointerId); } catch { /* not captured */ }
      handle.removeEventListener('pointermove', move);
      handle.removeEventListener('pointerup', up);
      handle.removeEventListener('pointercancel', up);
      handle.classList.remove('dragging');
      setCmdbarLines(cmdLines);
    };
    handle.addEventListener('pointermove', move);
    handle.addEventListener('pointerup', up);
    handle.addEventListener('pointercancel', up);
  });
  handle.addEventListener('dblclick', toggleCmdHistory);
  $('cmd-history-toggle').addEventListener('click', toggleCmdHistory);
  window.addEventListener('stenal-uilang', () => setCmdbarLines(cmdLines, false));
}

/** Run what was typed into the command bar. Empty repeats the last tool; a
 *  distance / coordinate goes to the drawing tool in progress; anything else is
 *  a command name or alias. */
function runCommand(raw: string): void {
  const text = raw.trim();
  if (!text) { repeatLastTool(); return; }
  // An option of the command in progress wins over a command of the same name,
  // as in AutoCAD: `U` inside LINE undoes a vertex, not the drawing.
  const opt = resolveOption(text, toolOptions(), tr);
  if (opt) { applyToolOption(opt); return; }
  // The Channel tool's figure asked for (laying runs, a whole number is how many).
  if (setChannelNumber(text)) return;
  // The Sculpted bench tool's: where it starts, then how long it is.
  if (setSculptNumber(text)) return;
  if (looksLikeEntry(text) && (drawDraft() || pendingRectCorner() || state.tool === 'offset')) {
    state.distEntry = text.toLowerCase();
    commitDistEntry();
    return;
  }
  const cmd = resolveCommand(text);
  if (!cmd) { cmdLog(`${tr('Unknown command')}: "${text}"`); return; }
  runCommandButton(cmd.button);
}

// ---- Command search (PowerPoint's "Tell me what you want to do") ----
// Whatever is typed in the bar also searches the commands — by name, alias, the
// toolbar label and its tooltip, in English and in the UI language — and lists
// what it finds above the bar. Enter runs the highlighted one (↑ / ↓ move it); a
// name typed exactly is always the top row, so AutoCAD typing is unchanged.
let cmdSuggest: CommandDef[] = [];
let cmdSuggestIdx = 0;

/** Every command whose button exists, with what it can be found by. */
function commandSearchEntries(): CommandSearchEntry[] {
  const head = (t: string): string => t.split(' — ')[0];
  const out: CommandSearchEntry[] = [];
  for (const cmd of COMMANDS) {
    const btn = document.getElementById(cmd.button);
    if (!btn) continue;
    const enTitle = btn.dataset.enTitle ?? btn.getAttribute('title') ?? '';
    // An icon-only button (Constrain) is named by its tooltip's head.
    const en = btn.dataset.en ?? (btn.textContent?.trim() || head(enTitle));
    out.push({ cmd, labels: [en, tr(en), head(enTitle), head(tr(enTitle))], hints: [enTitle, tr(enTitle)] });
  }
  return out;
}

/** A command as a person reads it: its toolbar label, translated, icon dropped. */
function commandLabel(cmd: CommandDef): string {
  const btn = document.getElementById(cmd.button);
  const title = (btn?.dataset.enTitle ?? btn?.getAttribute('title') ?? '').split(' — ')[0];
  const en = btn?.dataset.en ?? (btn?.textContent?.trim() || title || cmd.name);
  return tr(en).replace(/^[^\p{L}\p{N}]+/u, '');
}

/** Re-run the search for the bar's text. Hidden while the text is a distance /
 *  coordinate, an option of the command in progress, or the bar isn't focused. */
export function updateCmdSuggest(): void {
  const input = $<HTMLInputElement>('cmd-input');
  const text = input.value.trim();
  const off = !text || document.activeElement !== input || input.classList.contains('typed-entry')
    || looksLikeEntry(text) || !!resolveOption(text, toolOptions(), tr);
  cmdSuggest = off ? [] : searchCommands(text, commandSearchEntries());
  cmdSuggestIdx = 0;
  renderCmdSuggest(!off);
}

function renderCmdSuggest(show: boolean): void {
  let box = document.getElementById('cmd-suggest');
  if (!show) { box?.remove(); cmdSuggest = []; return; }
  if (!box) {
    box = document.createElement('div');
    box.id = 'cmd-suggest';
    document.body.appendChild(box);
  }
  const r = $('cmd-input').getBoundingClientRect();
  box.style.left = `${Math.max(4, r.left - 10)}px`;
  box.style.bottom = `${window.innerHeight - r.top + 4}px`;
  if (!cmdSuggest.length) {
    const d = document.createElement('div');
    d.className = 'cmd-sugg-empty';
    d.textContent = tr('No matching command');
    box.replaceChildren(d);
    return;
  }
  box.replaceChildren(...cmdSuggest.map((cmd, i) => {
    const row = document.createElement('div');
    const disabled = (document.getElementById(cmd.button) as HTMLButtonElement | null)?.disabled ?? false;
    row.className = `cmd-sugg${i === cmdSuggestIdx ? ' active' : ''}${disabled ? ' disabled' : ''}`;
    row.dataset.command = cmd.name;
    const label = document.createElement('span');
    label.textContent = commandLabel(cmd);
    const name = document.createElement('span');
    name.className = 'cmd-sugg-name';
    name.textContent = [cmd.name, ...cmd.aliases.slice(0, 2)].join(' · ');
    row.append(label, name);
    if (disabled) row.title = tr('not available now');
    // mousedown, not click: the input's blur (which closes the list) comes first.
    row.addEventListener('mousedown', (e) => { e.preventDefault(); runCmdSuggestion(i); });
    row.addEventListener('mousemove', () => {
      if (cmdSuggestIdx === i) return;
      cmdSuggestIdx = i;
      renderCmdSuggest(true);
    });
    return row;
  }));
}

/** Run a listed command, exactly as the toolbar would. */
function runCmdSuggestion(i: number): void {
  const cmd = cmdSuggest[i];
  const input = $<HTMLInputElement>('cmd-input');
  input.value = '';
  input.blur();
  renderCmdSuggest(false);
  if (cmd) runCommandButton(cmd.button);
}

export function wireCommandBar(): void {
  const cmdInput = $<HTMLInputElement>('cmd-input');
  installCmdbarResize();
  // While the text is a search that names no command exactly, Space is a space
  // (multi-word searches: "inner wall", "linea di quota") and Enter runs the
  // highlighted match.
  cmdInput.addEventListener('keydown', (e) => {
    if ((e.key === 'ArrowDown' || e.key === 'ArrowUp') && cmdSuggest.length) {
      e.preventDefault();
      const n = cmdSuggest.length;
      cmdSuggestIdx = (cmdSuggestIdx + (e.key === 'ArrowDown' ? 1 : n - 1)) % n;
      renderCmdSuggest(true);
      return;
    }
    if (e.key === ' ' && cmdSuggest.length && !resolveCommand(cmdInput.value)) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (cmdSuggest.length) { runCmdSuggestion(cmdSuggestIdx); return; }
      const text = cmdInput.value;
      cmdInput.value = '';
      cmdInput.blur();
      runCommand(text);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cmdInput.value = '';
      cmdInput.blur();
    }
  });
  cmdInput.addEventListener('input', updateCmdSuggest);
  cmdInput.addEventListener('blur', () => renderCmdSuggest(false));
  // Clicking into a typed distance / coordinate shown in the bar makes it plain,
  // editable text there; Enter then commits it through runCommand.
  cmdInput.addEventListener('focus', () => {
    if (!cmdInput.classList.contains('typed-entry')) return;
    cmdInput.classList.remove('typed-entry');
    state.distEntry = null;
    invalidate();
  });
}
